import { useEffect, useState } from "react";
import { Users, TrendingUp, DollarSign, Ticket, ShieldAlert, UserPlus, AlertTriangle, AlertCircle, FileWarning, Activity } from "lucide-react";
import AdminShell, { Panel } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { api } from "@/lib/api";

function fmtINR(v) {
  if (!Number.isFinite(v) || v === 0) return "₹0";
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000) return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const VOLUME_TABS = [
  { id: "spot", label: "Spot" },
  { id: "otc", label: "OTC" },
];

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [activity, setActivity] = useState([]);
  const [volTab, setVolTab] = useState("spot");

  useEffect(() => {
    api.get("/api/admin/dashboard/kpis").then(({ data }) => setKpis(data)).catch(() => {});
    api.get("/api/admin/dashboard/recent-activity").then(({ data }) => setActivity(data || [])).catch(() => {});
  }, []);

  return (
    <AdminShell title="Dashboard">
      {/* Row 1: 4 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="kpi-row-1">
        <KpiCard label="Total Users" value={kpis?.total_users ?? "—"} delta={kpis ? `+${kpis.new_users_last_month ?? 0}` : ""} deltaLabel="last 30d" icon={Users} variant="green" testid="kpi-total-users" />
        <KpiCard label="24h Trading Volume" value={fmtINR(kpis?.trading_volume_24h_inr)} delta="+8.5%" deltaLabel="vs yesterday" icon={TrendingUp} variant="yellow" testid="kpi-volume" />
        <KpiCard label="Today's Revenue" value={fmtINR(kpis?.revenue_today_inr)} delta="+15%" deltaLabel="vs yesterday" icon={DollarSign} variant="blue" testid="kpi-revenue" />
        <KpiCard label="Open Tickets" value={kpis?.open_tickets ?? 0} delta={`${kpis?.high_priority_tickets ?? 0}`} deltaLabel="high priority" icon={Ticket} variant="red" testid="kpi-tickets" />
      </div>
      {/* Row 2: 3 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="kpi-row-2">
        <KpiCard label="Pending KYC" value={kpis?.pending_kyc ?? 0} icon={ShieldAlert} variant="purple" testid="kpi-kyc" />
        <KpiCard label="New Users Today" value={kpis?.new_users_today ?? 0} delta="+23%" icon={UserPlus} variant="green" testid="kpi-new-users" />
        <KpiCard label="Compliance Alerts" value={kpis?.compliance_alerts ?? 0} icon={AlertTriangle} variant="red" testid="kpi-compliance" />
      </div>

      {/* Charts + Activity + System Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px_320px] gap-4">
        <Panel>
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-bold">Trading Volume (7 Days)</h3>
              <div className="flex gap-1 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] p-0.5">
                {VOLUME_TABS.map((t) => (
                  <button key={t.id} onClick={() => setVolTab(t.id)} className={`px-3 py-1 rounded-[6px] text-[11px] font-bold ${volTab === t.id ? "bg-[#F0B83A] text-[#0A0F1F]" : "text-[#8892A8]"}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="h-[280px] grid place-items-center text-[#5A6582] text-[13px] border border-dashed border-[#1E2845] rounded-[10px]">
              📊 Volume Chart — wires once Phase {volTab === "spot" ? "C" : "future"} is live
            </div>
          </div>
        </Panel>

        <Panel>
          <div className="p-5">
            <h3 className="text-[14px] font-bold mb-4">Recent Activity</h3>
            <div className="space-y-3" data-testid="recent-activity">
              {activity.length === 0 ? (
                <div className="text-[#5A6582] text-[13px]">No recent activity.</div>
              ) : activity.slice(0, 8).map((a, i) => (
                <div key={i} className="flex items-start gap-3 text-[13px]">
                  <div className="w-8 h-8 rounded-full bg-[#1A2138] grid place-items-center text-[#F0B83A] shrink-0">
                    {a.type === "user_registered" ? "👤" : a.type === "otc_inquiry" ? "💰" : "🎫"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{a.title}</div>
                    <div className="text-[11px] text-[#8892A8] truncate">{a.subtitle}</div>
                  </div>
                  <div className="text-[11px] text-[#5A6582] shrink-0">{timeAgo(a.at)}</div>
                </div>
              ))}
            </div>
          </div>
        </Panel>

        <Panel>
          <div className="p-5">
            <h3 className="text-[14px] font-bold mb-4">System Alerts</h3>
            <div className="space-y-3">
              <SysAlert icon={AlertCircle} color="#EF4444" title={`${kpis?.compliance_alerts ?? 1} Suspicious Activities`} sub="Requires immediate review" />
              <SysAlert icon={FileWarning} color="#F59E0B" title="1 Compliance Issues" sub="Pending compliance review" />
              <SysAlert icon={Activity} color="#3B82F6" title="1 System Warnings" sub="Performance optimization needed" />
            </div>
          </div>
        </Panel>
      </div>
    </AdminShell>
  );
}

function SysAlert({ icon: Icon, color, title, sub }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-[#0A0F1F] rounded-[10px] border border-[#1E2845]">
      <div className="w-8 h-8 rounded-[8px] grid place-items-center shrink-0" style={{ background: `${color}22`, color }}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold leading-tight">{title}</div>
        <div className="text-[10px] text-[#8892A8] mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
